/**
 * Sound / desktop notice when a new to-do arrives (T11-43 "เสียง/desktop notification เปิดได้ต่อ
 * staff"): a per-staff choice kept in this browser (localStorage), off by default. The header bell
 * calls `useInboxArrivals` with the badge counts; a higher `open` count than last time (not the
 * first load) plays a short tone and/or shows a browser notification.
 */
import { useEffect, useRef, useSyncExternalStore } from "react";

export interface InboxAlertPrefs {
  sound: boolean;
  desktop: boolean;
}

const OFF: InboxAlertPrefs = { sound: false, desktop: false };
const listeners = new Set<() => void>();
const cache = new Map<string, InboxAlertPrefs>();

export const alertPrefsKey = (staffId: string) => `tcg.admin.inbox-alerts.${staffId}`;

export function loadAlertPrefs(staffId: string): InboxAlertPrefs {
  const hit = cache.get(staffId);
  if (hit) {
    return hit;
  }
  let prefs = OFF;
  try {
    const raw = localStorage.getItem(alertPrefsKey(staffId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<InboxAlertPrefs>;
      prefs = { sound: parsed.sound === true, desktop: parsed.desktop === true };
    }
  } catch {
    prefs = OFF;
  }
  cache.set(staffId, prefs);
  return prefs;
}

export function saveAlertPrefs(staffId: string, prefs: InboxAlertPrefs): void {
  cache.set(staffId, prefs);
  try {
    localStorage.setItem(alertPrefsKey(staffId), JSON.stringify(prefs));
  } catch {
    // private mode / quota: the choice lasts for this tab only
  }
  for (const listener of listeners) {
    listener();
  }
}

export function useAlertPrefs(staffId: string): InboxAlertPrefs {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => loadAlertPrefs(staffId),
    () => OFF,
  );
}

/** Did new items arrive? (`previous` null = first answer — never alerts.) */
export function arrived(previous: number | null, current: number): boolean {
  return previous !== null && current > previous;
}

function playTone(): void {
  try {
    const AudioCtor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtor) {
      return;
    }
    const context = new AudioCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = 880;
    gain.gain.value = 0.08;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.2);
    oscillator.onended = () => void context.close();
  } catch {
    // no audio device / blocked until the first click — the badge still shows it
  }
}

function showDesktop(title: string, body: string): void {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    return;
  }
  try {
    new Notification(title, { body, tag: "tcg-admin-inbox" });
  } catch {
    // some browsers only allow notifications from a service worker
  }
}

/** Alert on new to-dos (the bell passes its open count; `texts` are already translated). */
export function useInboxArrivals(
  staffId: string,
  open: number | undefined,
  texts: { title: string; body: string },
): void {
  const prefs = useAlertPrefs(staffId);
  const previous = useRef<number | null>(null);
  useEffect(() => {
    if (open === undefined) {
      return;
    }
    if (arrived(previous.current, open)) {
      if (prefs.sound) {
        playTone();
      }
      if (prefs.desktop) {
        showDesktop(texts.title, texts.body);
      }
    }
    previous.current = open;
  }, [open, prefs.sound, prefs.desktop, texts.title, texts.body]);
}
